import Action from '../Action';

import style from './styles.module.css';

export const ButtonTheme = {
    PRIMARY: 'PRIMARY',
    SECONDARY: 'SECONDARY',
    GREY: 'GREY'
} as const;
export type ButtonThemeType = (typeof ButtonTheme)[keyof typeof ButtonTheme];

export const ButtonSize = {
    LEGACY: 'LEGACY',
    SMALL: 'SMALL',
    MEDIUM: 'MEDIUM',
    LARGE: 'LARGE'
} as const;
export type ButtonSizeType = (typeof ButtonSize)[keyof typeof ButtonSize];

/**
 * Shared design-system Button. The base props (`label`, `Icon`, `theme`,
 * `size`, `action`) cover most call sites; the rest are composable shape /
 * layout flags layered on top. Keep this file as the single source of
 * truth — when adding a new prop, write its JSDoc inline (right above the
 * prop) so future readers (and AI) can grasp it without a separate doc.
 */
interface ButtonProps {
    /** Visible text. When empty (or when `circle` is set), the button is
     *  rendered icon-only and the label, if any, becomes the `aria-label`. */
    label: string;
    /** Optional rich content rendered INSTEAD of `label`. Use when the
     *  visible text needs inline JSX (e.g. an embedded `GameNameWithIcon`
     *  cluster). `label` is still required because it doubles as the
     *  `aria-label` fallback for screen readers. */
    children?: React.ReactNode;
    /** Reserved for game cards / toggles where the button reflects a
     *  selected state. No visual effect by default — design system rules
     *  decide what `.active` means per context. */
    active?: boolean;
    /** Leading icon (left of the label by default). Combine with `IconRight`
     *  to also show a trailing icon. */
    Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    /** Trailing icon. When provided, `Icon` is forced to the left and this
     *  one sits on the right regardless of `iconPosition`. */
    IconRight?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    /** Position of `Icon` when `IconRight` is NOT set. Ignored when both
     *  icons are provided. Defaults to `'left'`. */
    iconPosition?: 'left' | 'right';
    /** Disables the click handler and applies the disabled visual. */
    disabled?: boolean;
    /** Extra class(es) appended to the button — for one-off layout tweaks
     *  from the caller (e.g. flex sizing, margins). */
    className?: string;
    /** Color variant. PRIMARY = filled, SECONDARY = outline, GREY = muted
     *  outline for dismissive / cancel actions. */
    theme?: ButtonThemeType;
    /** LEGACY (default, kept for back-compat), SMALL, MEDIUM, LARGE. Each
     *  size has its own height / padding / font-size in `styles.module.css`. */
    size?: ButtonSizeType;
    /** Click handler (function) OR navigation target (string path,
     *  triggers `history.push`). Resolved by the `<Action>` wrapper. */
    action?: string | (() => void);
    /** Stretch to the full width of the parent (drops min-width clamp).
     *  Use for primary CTAs in dialogs / panels. */
    fullWidth?: boolean;
    /** Switch the label font to Poppins-Bold + thicker icon strokes. */
    bold?: boolean;
    /** Flatten the top-left + bottom-left corners (button flush against a
     *  left edge — e.g. corner pill in a header). The left border is
     *  dropped so the bg visually touches the container. */
    halfLeft?: boolean;
    /** Mirror of `halfLeft`: flat right side, half-circle on the left. */
    halfRight?: boolean;
    /** Round ONLY the top-left corner as a quarter-circle (others
     *  square). For a corner-anchored button flush bottom/right, this
     *  curves the single inward-facing corner. */
    quarterLeft?: boolean;
    /** Mirror of `quarterLeft`: rounds only the top-right corner. */
    quarterRight?: boolean;
    /** Apply a 50% (round) border-radius. Alone: produces a full circle,
     *  icon-only, aspect-ratio 1, fixed touch-size; the label becomes the
     *  `aria-label`. Combined with `halfLeft` or `halfRight`: the flat side
     *  stays flat and only the rounded side gets a true half-circle. */
    circle?: boolean;
    /** Fully-rounded "pill" — both ends become true semicircles
     *  (border-radius clamped to height/2 by the browser) while the
     *  label stays visible. Unlike `circle` (icon-only, hides text),
     *  `pill` keeps text + icons and only changes the corner radius. */
    pill?: boolean;
    /** Drop the default `min-width` so the button shrinks to its intrinsic
     *  content (icon + text). Useful inside tight flex layouts where the
     *  clamp would otherwise force a too-wide button. */
    fitContent?: boolean;
    /** Let the label break onto multiple lines instead of being clipped
     *  with an ellipsis. The button's height becomes content-driven (with
     *  the original size-class height kept as `min-height` for the touch
     *  target). Wrapped text is left-aligned and uses a tight line-height. */
    wrapText?: boolean;
    /** Align inner content (icon + text) to the start of the button instead
     *  of the default center. Useful when stacking buttons in a column and
     *  you want their icons to line up vertically across rows (e.g. a list
     *  of choices in a speech bubble). */
    alignStart?: boolean;
    [key: string]: any;
}

export const Button = ({
    label = '',
    children = undefined,
    active = false,
    Icon = undefined,
    IconRight = undefined,
    iconPosition = 'left',
    disabled = false,
    className = '',
    theme = ButtonTheme.PRIMARY,
    size = ButtonSize.LEGACY,
    action = undefined,
    fullWidth = false,
    bold = false,
    halfLeft = false,
    halfRight = false,
    quarterLeft = false,
    quarterRight = false,
    circle = false,
    pill = false,
    fitContent = false,
    wrapText = false,
    alignStart = false,
    ...rest
}: ButtonProps) => {
    const parsedLabel = `${label}`.trim();
    const iconEl = Icon ? <Icon className={style.icon} /> : null;
    const iconRightEl = IconRight ? <IconRight className={style.icon} /> : null;
    // `circle` always hides the text — combined with halfLeft/halfRight it
    // produces an icon-only half-circle button, alone it's a full round one.
    const showText = !!parsedLabel && !circle;
    // When rich `children` are provided, they take precedence over
    // the plain label. The label is still kept as the `aria-label`
    // (set below) so assistive tech reads the same semantic text.
    const hasCustomContent = !circle && children !== undefined && children !== null;
    // When both icons are provided, force left/right placement; otherwise
    // fall back to the legacy single-icon + `iconPosition` behaviour.
    const leadingIcon = iconRightEl ? iconEl : iconPosition === 'left' ? iconEl : null;
    const trailingIcon = iconRightEl ?? (iconPosition === 'right' ? iconEl : null);

    const buttonClassName = `
            ${style.button}
            ${active ? style.active : ''}
            ${style[theme.toLowerCase()]}
            ${style[size.toLowerCase()]}
            ${halfLeft ? style.halfLeft : ''}
            ${halfRight ? style.halfRight : ''}
            ${quarterLeft ? style.quarterLeft : ''}
            ${quarterRight ? style.quarterRight : ''}
            ${circle ? style.circle : ''}
            ${pill ? style.pill : ''}
            ${fitContent ? style.fitContent : ''}
            ${wrapText ? style.wrapText : ''}
            ${alignStart ? style.alignStart : ''}
            ${disabled ? style.disabled : ''}
            ${showText ? style.withText : style.noText}
            ${fullWidth ? style.fullWidth : ''}
            ${bold ? style.bold : ''}
            ${className}
            `;
    const ariaLabel =
        (!showText || hasCustomContent) && parsedLabel ? parsedLabel : undefined;
    const content = (
        <>
            {leadingIcon}
            {hasCustomContent ? (
                <span className={style.text}>{children}</span>
            ) : (
                showText && <span className={style.text}>{parsedLabel}</span>
            )}
            {trailingIcon}
        </>
    );

    // Navigation (string action) keeps the <Action> wrapper: it renders a
    // semantic <Link>/<a>, which is the correct element for navigation.
    if (typeof action === 'string') {
        return (
            <Action
                className={buttonClassName}
                action={disabled ? undefined : action}
                aria-label={ariaLabel}
                {...rest}>
                {content}
            </Action>
        );
    }

    // Click actions render a NATIVE <button>: real button role, keyboard
    // focus and Enter/Space activation for free. `type="button"` prevents
    // implicit form submission (native default is `submit`); callers may
    // still pass `type="submit"` through the rest props.
    return (
        <button
            type="button"
            className={buttonClassName}
            onClick={disabled ? undefined : action}
            disabled={disabled}
            aria-label={ariaLabel}
            {...rest}>
            {content}
        </button>
    );
};

export default Button;
